/**
 * Two views of one file's sheet columns (`GET /files/:fileId/columns`):
 * - `source`: every column of the uploaded sheet, in order, with its detected
 *   type and example values;
 * - `mapping`: each column beside the standard column it is mapped to, and
 *   the ones that are not mapped.
 *
 * Laid out like the analysis picker: a search row, then a compact table that
 * is the only thing that scrolls.
 */

import { Box, Button, TextField } from '@mui/material';
import { useMemo, useState } from 'react';
import { useFileColumnsQuery } from '../../queries/intake';
import type { FileColumns } from '../../schemas';
import { Dialog } from '../../shared/ui';
import { count } from '../../utils/format';
import { Segmented } from '../shared/Segmented';
import { ErrorState, LoadingState } from '../shared/states';
import { DataTable, useFullScreen, type Column } from '../shared/tables';

export type FileColumnsView = 'source' | 'mapping';

type SheetColumn = FileColumns['columns'][number];
type MappedFilter = 'all' | 'mapped' | 'unmapped';

/** One line, at most `max` px wide, cut with an ellipsis; the full text on
 *  hover. The table sizes columns by content, so the cap is what keeps a long
 *  value from widening it. */
function Clipped({ text, max }: { text: string; max: number }) {
	return (
		<Box
			component="span"
			title={text}
			sx={{
				display: 'block',
				maxWidth: max,
				overflow: 'hidden',
				textOverflow: 'ellipsis',
				whiteSpace: 'nowrap',
			}}
		>
			{text}
		</Box>
	);
}

const POSITION: Column<SheetColumn> = {
	key: 'position',
	label: '#',
	num: true,
	width: 56,
	sortValue: (column) => column.position,
	render: (column) => column.position,
};

const NAME: Column<SheetColumn> = {
	key: 'name',
	label: 'Column in file',
	width: 240,
	sortValue: (column) => column.name,
	render: (column) => <Clipped text={column.name} max={220} />,
};

const SOURCE_COLUMNS: Array<Column<SheetColumn>> = [
	// POSITION,
	NAME,
	{
		key: 'type',
		label: 'Type',
		width: 96,
		sortValue: (column) => column.dataType,
		render: (column) => column.dataType || '—',
	},
	{
		key: 'samples',
		label: 'Example values',
		render: (column) =>
			column.samples.length > 0 ? (
				<Clipped text={column.samples.join(', ')} max={200} />
			) : (
				'—'
			),
	},
];

const MAPPING_COLUMNS: Array<Column<SheetColumn>> = [
	POSITION,
	NAME,
	{
		key: 'mappedTo',
		label: 'Mapped to standard column',
		sortValue: (column) => column.mappedTo?.label ?? '',
		render: (column) =>
			column.mappedTo ? (
				<Clipped text={column.mappedTo.label} max={440} />
			) : (
				<span className="mut">Not mapped</span>
			),
	},
];

export function FileColumnsDialog({
	fileId,
	fileName,
	view,
	onClose,
}: {
	fileId: string;
	fileName: string;
	view: FileColumnsView | null;
	onClose: () => void;
}) {
	const columns = useFileColumnsQuery(fileId, view !== null);
	const [search, setSearch] = useState('');
	const [mappedFilter, setMappedFilter] = useState<MappedFilter>('all');
	/* Passed so the table does not draw its own Full screen button. */
	const full = useFullScreen('File columns');

	const all = useMemo(() => columns.data?.columns ?? [], [columns.data]);
	const mapped = all.filter((column) => column.mappedTo !== null).length;

	const shown = useMemo(() => {
		const needle = search.trim().toLowerCase();
		return all.filter((column) => {
			if (view === 'mapping') {
				if (mappedFilter === 'mapped' && column.mappedTo === null)
					return false;
				if (mappedFilter === 'unmapped' && column.mappedTo !== null)
					return false;
			}
			if (!needle) return true;
			const haystack =
				view === 'mapping'
					? `${column.name} ${column.mappedTo?.label ?? 'not mapped'}`
					: `${column.name} ${column.dataType} ${column.samples.join(' ')}`;
			return haystack.toLowerCase().includes(needle);
		});
	}, [all, search, mappedFilter, view]);

	const close = () => {
		setSearch('');
		setMappedFilter('all');
		onClose();
	};

	return (
		<Dialog
			open={view !== null}
			onClose={close}
			width={900}
			plainFooter
			title={view === 'mapping' ? 'Column mapping' : 'Source columns'}
			subtitle={fileName}
			footer={
				<div className="spread">
					<span className="small mut">
						{view === 'mapping'
							? `${count(mapped)} mapped · ${count(all.length - mapped)} not mapped`
							: `${count(all.length)} columns`}
					</span>
					<Button variant="contained" onClick={close}>
						Close
					</Button>
				</div>
			}
		>
			{columns.isPending ? (
				<LoadingState label="Loading the columns…" />
			) : columns.error ? (
				<ErrorState
					error={columns.error}
					onRetry={() => void columns.refetch()}
				/>
			) : (
				<Box
					sx={{
						display: 'flex',
						flexDirection: 'column',
						gap: 'var(--s3)',
					}}
				>
					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'space-between',
							gap: 'var(--s2)',
							flexWrap: 'wrap',
						}}
					>
						<TextField
							type="search"
							size="small"
							placeholder={
								view === 'mapping'
									? 'Search column or standard column'
									: 'Search column or value'
							}
							value={search}
							onChange={(event) => setSearch(event.target.value)}
							slotProps={{
								htmlInput: { 'aria-label': 'Search columns' },
							}}
							sx={{ width: 300 }}
						/>
						{view === 'mapping' && (
							<Segmented<MappedFilter>
								value={mappedFilter}
								onChange={setMappedFilter}
								ariaLabel="Show columns"
								options={[
									{
										value: 'all',
										label: `All ${count(all.length)}`,
									},
									{
										value: 'mapped',
										label: `Mapped ${count(mapped)}`,
									},
									{
										value: 'unmapped',
										label: `Not mapped ${count(all.length - mapped)}`,
									},
								]}
							/>
						)}
					</Box>
					<DataTable
						rows={shown}
						columns={
							view === 'mapping'
								? MAPPING_COLUMNS
								: SOURCE_COLUMNS
						}
						rowKey={(column) => String(column.position)}
						caption={
							view === 'mapping'
								? 'Column mapping'
								: 'Source columns'
						}
						initialSort={{ key: 'position', direction: 'asc' }}
						maxHeight="compact"
						fullScreen={full}
						empty={
							all.length === 0
								? "This file's columns have not been read yet."
								: 'No column matches that search.'
						}
					/>
				</Box>
			)}
		</Dialog>
	);
}
